import { ControlledInputConfig } from '@/components/customByConfiguration';
import { ENTITY, QueryKey, UserActions } from '@/enums';
import { useValidationForm } from '@/hooks';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import { CreateUserEntity } from '@/interfaces';
import { RoleService, UserService } from '@/services';
import { CustomError, errorNotification } from '@/utils';
import { addNewAdminSchema } from '@/validations';
import { CustomButton } from '@amili-v3/amili-v3-design';
import { Form, notification } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminFormWrapper, FormNameWrapper } from './styles';
import { useGetRoleOptions } from './utils';

type CreateUserFormValues = Omit<CreateUserEntity, 'roles'> & { roles: string };

const FORM_ID = 'create-admin-form';
const baseInitialValues: CreateUserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  roles: '',
};

const AddNewAdminForm = (props: { closeModal: () => void }) => {
  const { closeModal } = props;
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const { data: profile } = useGetMe();
  const level = profile?.mainRole?.level;
  const { data: roleData, isLoading } = useQuery({
    queryKey: [
      QueryKey.ADMINISTRATOR.ROLE,
      Math.max(level ?? 0, level ?? 0).toString(),
    ],
    queryFn: RoleService.getRolesWithLimitLevel,
  });

  const { canUpdateUsers: canAssignRole } = useCheckScopeCombinations([
    [{ entity: ENTITY.USERS, action: UserActions.UPDATE }],
  ]);

  const mutationCreate = useMutation({
    mutationFn: UserService.createUser,
    onSuccess: () => {
      notification.success({ message: t('common.success') });
      queryClient.invalidateQueries({ queryKey: [QueryKey.ADMINISTRATOR.ALL] });
      closeModal();
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const isSetDefaultRole = !canAssignRole;
  const defaultRoleId = String(roleData?.data[0]?.id) ?? '';
  const initialValues = {
    ...baseInitialValues,
    roles: isSetDefaultRole ? defaultRoleId : baseInitialValues.roles,
  };

  const handleSubmit = (values: CreateUserFormValues) => {
    const newValues: CreateUserEntity = {
      ...values,
      roles: isSetDefaultRole ? [defaultRoleId] : [values.roles],
    };
    return mutationCreate.mutateAsync(newValues);
  };

  const adminOptions = useGetRoleOptions(roleData?.data ?? []);

  const renameLabelAdminOptions = adminOptions.map(admin => {
    return {
      label: i18n.exists(`roles.${admin.label}`)
        ? t(`roles.${admin.label}`)
        : admin.label,
      value: admin.value,
    };
  });

  const ConfigSchema = useValidationForm(FORM_ID, addNewAdminSchema);

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={ConfigSchema}
    >
      {({ handleSubmit: handleFinish }) => {
        return (
          <AdminFormWrapper>
            <Form onFinish={handleFinish} layout="vertical" id={FORM_ID}>
              <FormNameWrapper>
                <ControlledInputConfig
                  required
                  label="Name"
                  name="firstName"
                  aria-label="firstName"
                  placeholder={t('common.firstName') as string}
                />
                <ControlledInputConfig
                  hideLabel
                  label="_"
                  name="lastName"
                  aria-label="lastName"
                  placeholder={t('common.lastName') as string}
                />
              </FormNameWrapper>
              <ControlledInputConfig
                required
                name="email"
                aria-label="email"
                label={t('common.emailAddress')}
                placeholder="email@company.com"
              />
              {canAssignRole && (
                <ControlledInputConfig
                  required
                  isPreventCustomConfig
                  name="roles"
                  aria-label="roles"
                  inputType="select"
                  loading={isLoading}
                  placeholder="Select"
                  options={renameLabelAdminOptions}
                  label={t('administrator.adminRoles')}
                />
              )}

              <CustomButton
                width="100%"
                size="large"
                key="create"
                htmlType="submit"
                loading={mutationCreate.isPending}
              >
                {t('administrator.createAdmin')}
              </CustomButton>
            </Form>
          </AdminFormWrapper>
        );
      }}
    </Formik>
  );
};

export default AddNewAdminForm;
